import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Phone, Mail, MapPin, CheckCircle2, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getProductBySlug, products as allProducts } from '../data/productsData';

const ProductDetail = () => {
  const { slug } = useParams();
  const product = getProductBySlug(slug);

  if (!product) {
    return <Navigate to="/products" replace />;
  }

  const quoteHref = `/contact?type=${product.contactType}`;
  const categoryLabel = product.category === 'commercial' ? 'Commercial Insurance' : 'Personal Insurance';
  const categoryHref = `/products?tab=${product.category}`;

  // Other products to show at the bottom (same-category first, then opposite category)
  const otherProducts = Object.values(allProducts).filter((p) => p.slug !== product.slug);
  const sameCategory = otherProducts.filter((p) => p.category === product.category);
  const otherCategory = otherProducts.filter((p) => p.category !== product.category);
  const browseProducts = [...sameCategory, ...otherCategory];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to={categoryHref}
            className="inline-flex items-center gap-2 text-slate-300 hover:text-[#C9A361] transition-colors mb-6 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to {categoryLabel}
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            <div>
              <p className="text-[#C9A361] font-semibold uppercase tracking-wider text-sm mb-4">
                {categoryLabel}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                {product.name}
              </h1>
              <p className="text-xl text-slate-300 mb-8">
                {product.tagline}
              </p>
              <Link to={quoteHref}>
                <Button
                  size="lg"
                  className="bg-[#C9A361] hover:bg-[#B8925A] text-white px-8 py-6 text-lg transition-all hover:scale-105"
                >
                  Get a Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            {product.heroImage && (
              <div className="relative">
                <div className="absolute inset-0 bg-[#C9A361] rounded-2xl translate-x-4 translate-y-4 opacity-50" />
                <img
                  src={product.heroImage}
                  alt={product.name}
                  className="relative rounded-2xl shadow-2xl w-full h-80 lg:h-96 object-cover"
                  loading="eager"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Intro + Quick Contact Panel */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main content */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                {product.name}: The Right Fit for You
              </h2>
              {product.intro.map((paragraph, idx) => (
                <p key={idx} className="text-slate-600 leading-relaxed mb-4 text-lg">
                  {paragraph}
                </p>
              ))}

              <h3 className="text-2xl font-bold text-slate-900 mt-10 mb-6">
                Key Features of Our Coverage
              </h3>
              <ul className="space-y-4">
                {product.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-[#C9A361] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10">
                <Link to={quoteHref}>
                  <Button
                    size="lg"
                    className="bg-[#C9A361] hover:bg-[#B8925A] text-white px-8 py-6 text-lg transition-all hover:scale-105"
                  >
                    Get a Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Sticky quick contact card */}
            <div>
              <Card className="border-2 border-[#C9A361] shadow-lg lg:sticky lg:top-24">
                <CardContent className="p-8">
                  <p className="text-sm font-semibold uppercase tracking-wider text-[#C9A361] mb-2">
                    Quick Insurance Process
                  </p>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">
                    Talk to an expert
                  </h3>
                  <a
                    href="tel:954-495-2929"
                    className="block text-3xl font-bold text-slate-900 hover:text-[#C9A361] transition-colors mb-6"
                  >
                    954-495-2929
                  </a>
                  <div className="space-y-3 text-sm">
                    <a
                      href="mailto:contact@progressinsure.com"
                      className="flex items-center gap-2 text-slate-600 hover:text-[#C9A361] transition-colors break-all"
                    >
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      contact@progressinsure.com
                    </a>
                    <div className="flex items-start gap-2 text-slate-600">
                      <MapPin className="w-4 h-4 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-semibold text-slate-700">Coral Springs Office</p>
                        <p className="mb-2">3111 N University Dr Ste 404, Coral Springs, FL 33065</p>
                        <p className="font-semibold text-slate-700">Maitland Office</p>
                        <p>500 N Maitland Ave Ste 304, Maitland, FL 32751</p>
                      </div>
                    </div>
                  </div>
                  <Link to={quoteHref} className="block mt-6">
                    <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white py-6">
                      Request Free Quote
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Supporting sections */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {product.sections.map((section, idx) => (
              <Card key={idx} className="border-2 hover:border-[#C9A361] transition-all">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-slate-900 mb-4">
                    {section.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {section.body}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Browse Other Products */}
      <section className="py-16 lg:py-20 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4">
            <div>
              <p className="text-[#C9A361] font-semibold uppercase tracking-wider text-sm mb-2">
                Explore More Coverage
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
                Other Insurance Products
              </h2>
            </div>
            <Link to="/products" className="text-[#C9A361] hover:text-[#B8925A] font-semibold text-sm inline-flex items-center gap-1 transition-colors">
              View all products
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {browseProducts.map((p) => (
              <Link key={p.slug} to={`/products/${p.slug}`} className="block group">
                <Card className="h-full border-2 hover:border-[#C9A361] transition-all hover:shadow-xl overflow-hidden">
                  {p.heroImage && (
                    <div className="h-40 overflow-hidden bg-slate-100">
                      <img
                        src={p.heroImage}
                        alt={p.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <CardContent className="p-6">
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#C9A361] mb-2">
                      {p.category === 'commercial' ? 'Commercial' : 'Personal'}
                    </p>
                    <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-[#C9A361] transition-colors">
                      {p.name}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed line-clamp-2">
                      {p.tagline}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-3 text-[#C9A361] font-semibold text-sm group-hover:gap-2 transition-all">
                      Learn more <ArrowRight className="w-4 h-4" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Get your {product.name.toLowerCase()} quote today
          </h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">
            Tell us a little about your needs and our team will reach out with options tailored to you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={quoteHref}>
              <Button
                size="lg"
                className="bg-[#C9A361] hover:bg-[#B8925A] text-white px-8 py-6 text-lg transition-all hover:scale-105"
              >
                Get a Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:954-495-2929">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white px-8 py-6 text-lg transition-all"
              >
                <Phone className="mr-2 w-5 h-5" />
                954-495-2929
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;
